import type { ErrorData } from "@vkontakte/vk-bridge";
import type { IPlatformPurchaseDescriptor, PlatformPurchaseStep } from "./PlatformPurchaseError";

/** Код client_error VK Bridge «пользователь отказался» (https://dev.vk.com/ru/bridge/getting-started) */
const VK_BRIDGE_USER_DENIED_ERROR_CODE = 4;

/**
 * Является ли значение ошибкой VK Bridge
 * @param error значение, которым отклонён vkBridge.send
 */
function isVkBridgeError(error: unknown): error is ErrorData {
	return typeof error === "object" && error !== null && "error_type" in error && "error_data" in error;
}

/**
 * Отказ игрока в окне оплаты VK
 * @param step шаг покупки
 * @param error исходная ошибка шага
 */
function isVkBridgeUserCancellation(step: PlatformPurchaseStep, error: unknown): boolean {
	return (
		step === "purchase" &&
		isVkBridgeError(error) &&
		error.error_type === "client_error" &&
		error.error_data.error_code === VK_BRIDGE_USER_DENIED_ERROR_CODE
	);
}

/**
 * Текст ошибки VK Bridge: error_msg у api_error, причина и описание у client_error и auth_error
 * @param error исходная ошибка шага
 */
function vkBridgeErrorText(error: unknown): string | undefined {
	if (!isVkBridgeError(error)) {
		return undefined;
	}
	switch (error.error_type) {
		case "api_error":
			return error.error_data.error_msg;
		case "client_error":
			return [error.error_data.error_reason, error.error_data.error_description].filter(Boolean).join(" ");
		case "auth_error":
			return [error.error_data.error_reason, ...(error.error_data.error_description ?? [])].filter(Boolean).join(" ");
		default:
			// Неизвестный error_type: текст соберётся из JSON ошибки
			return undefined;
	}
}

/**
 * Код ошибки VK Bridge: error_type и error_code
 * @param error исходная ошибка шага
 */
function vkBridgeSourceCode(error: unknown): string | undefined {
	return isVkBridgeError(error) ? `${error.error_type}_${error.error_data.error_code}` : undefined;
}

/**
 * Правила покупки через окно оплаты VK Bridge (VKWebAppShowOrderBox)
 * @param platform vkcom — VK.com, ok — Одноклассники через кабинет ВК
 */
export function createVkBridgePurchaseDescriptor(platform: "vkcom" | "ok"): IPlatformPurchaseDescriptor {
	return {
		platform,
		source: "vk-bridge",
		isCancellation: isVkBridgeUserCancellation,
		errorText: vkBridgeErrorText,
		sourceCode: vkBridgeSourceCode
	};
}
